<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ $t("firm.management") }}</h2>
    </div>

    <div class="d-flex  mt-4 justify-content-between">
      <div class="search2">
        <div class="search2--icon"><i class="iconoir-search"></i></div>
        <input v-model="nameFilter" class="search2--input" type="text" :placeholder="$t('common.search')" />
      </div>
    </div>

    <div v-if="summaryDocs.length" class="row mt-4 mb-4">
      <div v-for="(item, idx) in summaryDocs" :key="item.firm._id" class="col-md-4">
        <div class="firm-card">
          <div class="firm-card__top">
            <div class="firm-card__index">
              <h4>{{ item.firm.name | translate }}</h4>
            </div>

            <firm-status-select :selected="getFirmStatusForItem(item)" @onSelect="(val) => onStatusSelect(val, item)" />
          </div>
          <div class="firm-card__created">{{ $t("common.created-at") }}: {{ formatDate(item.createdAt || item.firm.createdAt) }}</div>

          <div class="firm-card__list">
            <div class="firm-card__row" v-for="row in cardRows(item)" :key="row.key">
              <div class="firm-card__icon" :class="{ 'is-muted': row.muted }">
                <i :class="row.icon"></i>
              </div>
              <div>
                <div class="firm-card__label" :class="{ 'is-muted': row.muted }">{{ row.label }}</div>
                <div class="firm-card__value" :class="{ 'is-muted': row.muted }">{{ row.value }}</div>
              </div>
            </div>
          </div>

          <div class="firm-card__stats">
            <div class="firm-card__stat">
              <div class="stat-label">{{ $t("firm.audit.active-count") }}</div>
              <div class="stat-value">{{ formatNumber(item.activeAuditCount) }}</div>
            </div>
            <div class="firm-card__stat">
              <div class="stat-label">{{ $t("firm.audit.completed-count") }}</div>
              <div class="stat-value">{{ formatNumber(item.completedAuditCount) }}</div>
            </div>
            <!-- <div class="firm-card__stat">
              <div class="stat-label">{{ $t("firm.audit.dashboard.approved-questions") }}</div>
              <div class="stat-value">{{ formatNumber(item.approvedQuestionCount) }}</div>
            </div> -->
          </div>

          <div class="firm-card__actions">
            <b-button variant="outline-main" size="sm" class="firm-card__btn" @click="goToAuditManagement(item.firm)">
              {{ $t("firm.audit.management") }}
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <content-loading v-if="isSummaryLoading" />

    <empty v-if="!isSummaryLoading && (!summary || !summary.docs || !summary.docs.length)">
      <h5 class="text-center">{{ $t("common.empty") }}</h5>
    </empty>

    <div v-if="summary" class="row mb-5 pb-5">
      <div class="col">
        <ul class="pagination pagination-rounded mb-0">
          <b-pagination v-model="request.options.page" :total-rows="summary.totalDocs" :per-page="summary.limit" limit="10" size="sm"></b-pagination>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { getFirmStatus, getStatusFieldByProduct } from "@/helpers/filters";
import ContentLoading from "@c/content-loading.vue";
import Empty from "@c/empty.vue";
import FirmStatusSelect from "@cc/firm-status-select";

export default {
  components: { ContentLoading, Empty, FirmStatusSelect },
  data() {
    return {
      nameFilter: undefined,
      request: {
        options: { page: 1, limit: 15, sort: { "firm.name": "asc" } },
        query: {},
      },
    };
  },
  computed: {
    summary() {
      return this.$store.getters["organizationFirm/summaryPagination"];
    },
    summaryDocs() {
      return this.summary?.docs ?? [];
    },
    isSummaryLoading() {
      return this.$store.getters["organizationFirm/isSummaryLoading"];
    },
    currentProduct() {
      return this.$store.getters["product/currentProduct"] || "FWSED";
    },
  },
  watch: {
    nameFilter(n, o) {
      if (n) {
        this.request.query = { ...this.request.query, name: { value: n } };
      } else {
        this.request.query["name"] = null;
        delete this.request.query["name"];
      }
    },
    request: {
      deep: true,
      immediate: true,
      handler() {
        this.debounceMixin(() => this.fetchSummary());
      },
    },
  },
  methods: {
    async onStatusSelect(event, item) {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return await this.fetchSummary();
      }

      try {
        // Dynamically determine status field based on product
        const statusField = getStatusFieldByProduct(this.currentProduct);
        const payload = {
          firmId: item?.firm?._id,
          [statusField]: event,
        };
        await this.$store.dispatch("organizationFirm/updateStatus", payload);
        await this.fetchSummary();
        this.$toast.success(this.$t("common.success"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    getFirmStatusForItem(item) {
      return getFirmStatus(item?.firm, this.currentProduct);
    },

    goToAuditManagement(firm) {
      this.$store.commit("organizationFirm/selectedEntity", firm);
      this.$router.push(`/fwsed/firm-management/${firm._id}/audit`);
    },

    async fetchSummary() {
      await this.$store.dispatch("organizationFirm/fetchSummary", this.request);
    },
    formatDate(value) {
      if (!value) return "-";
      return this.$options.filters.date(value, true);
    },
    formatBoolean(value) {
      return value ? this.$t("common.yes") : this.$t("common.no");
    },
    formatNumber(value) {
      if (value === null || value === undefined) return "-";
      const number = Number(value);
      if (Number.isNaN(number)) return "-";
      return number.toLocaleString("tr-TR");
    },
    cardRows(item) {
      const firm = item.firm || {};
      const auditStartedAt = item.auditStartedAt;
      const testReadyAt = item.testReadyAt || firm.testApprovedAt;
      const prodReadyAt = item.prodReadyAt || firm.prodApprovedAt;

      const rows = [
        {
          key: "supportService",
          label: this.$t("firm.support-service"),
          value: this.formatBoolean(item.supportService ?? firm.supportService),
          icon: "mdi mdi-help text-main",
          muted: false,
        },
        {
          key: "virtualServer",
          label: this.$t("firm.virtual-server"),
          value: this.formatBoolean(item.virtualServer ?? firm.virtualServer),
          icon: "mdi mdi-server text-main",
          muted: false,
        },
        {
          key: "auditStart",
          label: this.$t("firm.audit.audit-start"),
          value: this.formatDate(auditStartedAt),
          icon: "mdi mdi-calendar-blank text-main",
          muted: !auditStartedAt,
        },
        {
          key: "testReady",
          label: this.$t("firm.test-approval-date"),
          value: this.formatDate(testReadyAt),
          icon: "mdi mdi-flask-outline text-main",
          muted: !testReadyAt,
        },
        {
          key: "prodReady",
          label: this.$t("firm.prod-approval-date"),
          value: this.formatDate(prodReadyAt),
          icon: "mdi mdi-rocket text-main",
          muted: !prodReadyAt,
        },
      ];

      return rows;
    },
  },
};
</script>

<style lang="scss" scoped>
.firm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
}

.firm-card {
  background: #fff;
  border-radius: 18px;
  padding: 1.5rem;
  box-shadow: 0 12px 36px rgba(15, 23, 42, 0.08);
  border: 1px solid #eef2f7;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.firm-card__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.firm-card__index {
  color: #3e4b72;
}

.firm-card__status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
  background: #eef2f7;
  color: #475569;
}

.firm-card__created {
  color: #6c7280;
  font-weight: 600;
  font-size: 0.95rem;
}

.firm-card__list {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.firm-card__row {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 0.75rem;
  align-items: center;
}

.firm-card__icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f4f6fb;
  display: grid;
  place-items: center;
  color: #6a6f85;
  font-size: 1.1rem;
}

.firm-card__label {
  font-weight: 700;
  color: #374151;
}

.firm-card__value {
  color: #6b7280;
  font-weight: 600;
  font-size: 0.95rem;
}

.firm-card__stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  text-align: center;
  padding-top: 0.75rem;
  border-top: 1px solid #eef2f7;
}

.firm-card__stat {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.stat-label {
  color: #6b7280;
  font-size: 0.9rem;
}

.stat-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
}

.firm-card__actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.firm-card__btn {
  flex: 1 1 auto;
}

.firm-card__icon.is-muted,
.firm-card__label.is-muted,
.firm-card__value.is-muted {
  opacity: 0.55;
}
</style>
